
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function AccessApplication() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyLinkedin: '',
    investmentRange: '',
    interestType: '',
    timeline: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    setIsSubmitted(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (isSubmitted) {
    return (
      <section className="py-32 relative overflow-hidden">
        <div className="container mx-auto max-w-4xl px-6">
          <div className="flex justify-center">
            <div className="w-full max-w-2xl">
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-16 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-8">
                  <i className="ri-check-line text-3xl text-white"></i>
                </div>
                <h2 className="text-3xl font-light mb-6">Application Received</h2>
                <p className="text-xl text-gray-300 leading-relaxed mb-8">
                  Thank you — your request has been received. Our team reviews new members within 24 hours. 
                  If approved, you'll receive your login credentials via email.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-32 relative overflow-hidden">
      <div className="container mx-auto max-w-4xl px-6">
        {/* Urgency Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-3 bg-red-500/20 border border-red-400/30 rounded-full px-6 py-3 mb-6">
            <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse"></div>
            <span className="text-red-300 font-medium">Limited spots available - Apply now</span>
          </div>
        </div>

        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-6">
            Apply for <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent font-medium">Private Access</span>
          </h2>
          <p className="text-xl text-gray-300 mb-6">
            Complete this brief application to join our exclusive network
          </p>
        </div>

        {/* Application Form */}
        <div className="flex justify-center">
          <div className="w-full max-w-3xl">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12">
              {/* Fast Track Notice */}
              <div className="bg-blue-500/20 border border-blue-400/30 rounded-xl p-4 mb-8">
                <div className="flex items-center space-x-3">
                  <i className="ri-flashlight-line text-blue-400"></i>
                  <div>
                    <p className="text-blue-300 font-medium">Fast Track Review</p>
                    <p className="text-sm text-gray-300">Complete applications reviewed within 24 hours</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full bg-white/5 border border-white/20 rounded-xl px-6 py-4 text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-white/5 border border-white/20 rounded-xl px-6 py-4 text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200"
                    placeholder="your@email.com"
                  />
                </div>

                {/* Company / LinkedIn */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Company / LinkedIn URL *
                  </label>
                  <input
                    type="url"
                    name="companyLinkedin"
                    value={formData.companyLinkedin}
                    onChange={handleChange}
                    required
                    className="w-full bg-white/5 border border-white/20 rounded-xl px-6 py-4 text-white placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200"
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>

                {/* Investment Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Investment Range *
                  </label>
                  <div className="relative">
                    <select
                      name="investmentRange"
                      value={formData.investmentRange}
                      onChange={handleChange}
                      required
                      className="w-full bg-white/5 border border-white/20 rounded-xl px-6 py-4 pr-8 text-white focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200 appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-gray-900">Select investment range</option>
                      <option value="100k-250k" className="bg-gray-900">$100K - $250K</option>
                      <option value="250k-500k" className="bg-gray-900">$250K - $500K</option>
                      <option value="500k-1m" className="bg-gray-900">$500K - $1M</option>
                      <option value="1m+" className="bg-gray-900">$1M+</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>

                {/* Interest Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Interest Type *
                  </label>
                  <div className="relative">
                    <select
                      name="interestType"
                      value={formData.interestType}
                      onChange={handleChange}
                      required
                      className="w-full bg-white/5 border border-white/20 rounded-xl px-6 py-4 pr-8 text-white focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200 appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-gray-900">Select your interest</option>
                      <option value="buy" className="bg-gray-900">Buy</option>
                      <option value="build" className="bg-gray-900">Build</option>
                      <option value="partner" className="bg-gray-900">Partner</option>
                      <option value="invest" className="bg-gray-900">Invest</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Timeline to Act *
                  </label>
                  <div className="relative">
                    <select
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      required
                      className="w-full bg-white/5 border border-white/20 rounded-xl px-6 py-4 pr-8 text-white focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200 appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-gray-900">Select timeline</option>
                      <option value="this-month" className="bg-gray-900">This Month</option>
                      <option value="next-3-months" className="bg-gray-900">Next 3 Months</option>
                      <option value="exploring" className="bg-gray-900">Exploring Options</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-8">
                  <button 
                    onClick={() => window.open('https://form.typeform.com/to/p6aQCAhU', '_blank')}
                    className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-8 py-4 rounded-full font-medium hover:scale-105 transition-transform duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Apply for Access
                  </button>
                  
                  {/* Trust Signals */}
                  <div className="flex items-center justify-center space-x-6 mt-6 text-xs text-gray-400">
                    <div className="flex items-center space-x-2">
                      <i className="ri-shield-check-line text-green-400"></i>
                      <span>Secure & Confidential</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <i className="ri-time-line text-blue-400"></i>
                      <span>24hr Review</span>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
